'use client';

import type { KeyboardEvent, ReactNode } from 'react';
import { useId, useRef, useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

export type FillingTab = {
  id: number;
  /** Plain text tab label (machine name). */
  label: string;
  /** Server-rendered panel content. */
  panel: ReactNode;
};

const KEY_STEP: Record<string, number> = {
  ArrowRight: 1,
  ArrowLeft: -1,
};

/**
 * Figma 4680:4315 „Prepínač“ — machine switch of the filling section: a #f2f3ef bar with the
 * active tab on white. The tabs share the bar width from `lg` and scroll sideways below it
 * (mobile 4704:4136). Automatic activation: arrows, Home and End move focus and select.
 */
export function FillingTabs({ tabs, labelledBy }: { tabs: FillingTab[]; labelledBy: string }) {
  const baseId = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    setActive(index);
    const tab = tabRefs.current[index];
    tab?.focus();
    tab?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = tabs.length;
    let next: number | null = null;
    if (event.key in KEY_STEP) next = (index + KEY_STEP[event.key] + count) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    if (next === null) return;

    event.preventDefault();
    select(next);
  };

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      <div className="hide-scroll-bar overflow-x-auto">
        <div
          role="tablist"
          aria-labelledby={labelledBy}
          className="flex w-max min-w-full gap-1.5 rounded-[15px] rounded-tr-none bg-[#f2f3ef] p-1.5 lg:w-full lg:rounded-[20px] lg:rounded-tr-none"
        >
          {tabs.map((tab, index) => {
            const isActive = index === active;

            return (
              <button
                key={tab.id}
                ref={element => {
                  tabRefs.current[index] = element;
                }}
                type="button"
                role="tab"
                id={`${baseId}-tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`${baseId}-panel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => select(index)}
                onKeyDown={event => onKeyDown(event, index)}
                className={cn(
                  'svt-button shrink-0 whitespace-nowrap rounded-[12px] rounded-tr-none px-5 py-3.5 transition-[color,background-color,box-shadow] lg:flex-1 lg:rounded-[15px] lg:rounded-tr-none',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2c7a25] motion-reduce:transition-none',
                  isActive
                    ? 'bg-white text-[#14171a] shadow-[0_4px_16px_rgba(13,26,13,0.1)]'
                    : 'text-[#4a5560] hover:text-[#14171a]',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {tabs.map((tab, index) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${baseId}-panel-${tab.id}`}
          aria-labelledby={`${baseId}-tab-${tab.id}`}
          tabIndex={0}
          hidden={index !== active}
          className="rounded-[20px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-4"
        >
          {tab.panel}
        </div>
      ))}
    </div>
  );
}
